/**
 * Site configuration: the single source of truth for links, contact details and feature switches.
 *
 * Defaults live here. The site owner overrides a subset at runtime through `window.RT_*` values in
 * `/assets/site-config.js` (docs/WEBSITE-SETTINGS-GUIDE.md). Do not invent URLs: a value that ReTax360
 * has not supplied stays empty ("" or "#" means "not set").
 */
import { logger } from '../lib/logger';

export type RoutingMode = 'hash' | 'path';

/** Maps our form field names to Jotform field names. `_extra` collects unmapped fields in one field. */
export type JotformFieldMap = Readonly<Record<string, string>>;

export interface PostalAddress {
  readonly street: string;
  readonly city: string;
  readonly region: string;
  readonly postalCode: string;
  readonly country: string;
}

export interface SiteConfig {
  /** "hash" = single-file preview (#/platform). "path" = production hosting (/platform). */
  readonly ROUTING: RoutingMode;
  readonly FORMSPREE_ID: string;
  readonly HUBSPOT_PORTAL_ID: string;
  readonly HUBSPOT_FORM_ID: string;
  /** Default form delivery: every submission is emailed here through FormSubmit. */
  readonly FORM_EMAIL: string;
  /** Optional FormSubmit alias that hides the address (from the activation email). */
  readonly FORM_EMAIL_ALIAS: string;
  readonly JOTFORM_FORM_ID: string;
  readonly JOTFORM_FIELDS: JotformFieldMap | null;
  /** Google Analytics 4 measurement ID. Empty = no analytics and no cookie banner. */
  readonly GA4_ID: string;
  /** YouTube video ID. Empty = the platform video section stays hidden. */
  readonly PLATFORM_VIDEO_ID: string;
  readonly MEDIUM_FEED: boolean;
  readonly INSIGHTS_JSON_URL: string;
  readonly SECURITY_PDF_URL: string;
  /** Sales appointment schedule. If emptied, scheduling CTAs fall back to /demo. */
  readonly SALES_CALENDAR_URL: string;
  /** Embeddable version of the calendar. Empty = button-only fallback. */
  readonly SALES_CALENDAR_EMBED_URL: string;
  readonly LINKEDIN_URL: string;
  /** Founder's account: label accurately, never as a company account. */
  readonly MEDIUM_URL: string;
  readonly YOUTUBE_URL: string;
  readonly INSTAGRAM_URL: string;
  readonly SALES_EMAIL: string;
  readonly INFO_EMAIL: string;
  readonly CAREERS_EMAIL: string;
  readonly CAREERS_MAILTO: string;
  readonly LEGAL_NAME: string;
  readonly ADDRESS: PostalAddress;
  readonly SITE_URL: string;
  readonly COPYRIGHT_YEAR: number;
}

/** The subset of `window` that carries owner settings. */
export type RuntimeSettings = Pick<
  Window,
  | 'RT_ROUTING'
  | 'RT_FORM_EMAIL'
  | 'RT_FORM_EMAIL_ALIAS'
  | 'RT_FORMSPREE_ID'
  | 'RT_HUBSPOT_PORTAL_ID'
  | 'RT_HUBSPOT_FORM_ID'
  | 'RT_JOTFORM_FORM_ID'
  | 'RT_JOTFORM_FIELDS'
  | 'RT_GA4_ID'
  | 'RT_PLATFORM_VIDEO_ID'
  | 'RT_MEDIUM_FEED'
  | 'RT_INSIGHTS_JSON_URL'
  | 'RT_SECURITY_PDF_URL'
  | 'RT_SALES_CALENDAR_URL'
  | 'RT_SALES_CALENDAR_EMBED_URL'
>;

const SALES_CALENDAR_SCHEDULE = 'AcZssZ3plXnbkHjd8l24TlRSR9l8epLqI6GBND57IqohZqMCoTc4k4gXD3Rh8RFDmORQCuIx6zYMUHGy';

const DEFAULTS = {
  SALES_CALENDAR_URL: `https://calendar.google.com/calendar/u/0/appointments/schedules/${SALES_CALENDAR_SCHEDULE}`,
  SALES_CALENDAR_EMBED_URL: `https://calendar.google.com/calendar/appointments/schedules/${SALES_CALENDAR_SCHEDULE}?gv=true`,
  FORM_EMAIL: 'sales@retax360.com',
} as const;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const GA4_ID_PATTERN = /^G-[A-Z0-9]+$/;

/** A non-empty string setting, or the fallback. Mirrors the historical `window.RT_X || default` rule. */
function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value !== '' ? value : fallback;
}

function jotformFields(value: unknown): JotformFieldMap | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const entries = Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string');
  return Object.freeze(Object.fromEntries(entries));
}

/** Reports settings that are set but malformed, so a typo in site-config.js is visible in the console. */
function validate(config: SiteConfig): void {
  const problems: string[] = [];
  if (config.FORM_EMAIL && !EMAIL_PATTERN.test(config.FORM_EMAIL))
    problems.push('RT_FORM_EMAIL is not an email address');
  if (config.GA4_ID && !GA4_ID_PATTERN.test(config.GA4_ID)) problems.push('RT_GA4_ID should look like "G-ABC123XYZ"');
  if (config.JOTFORM_FORM_ID && !config.JOTFORM_FIELDS)
    problems.push('RT_JOTFORM_FORM_ID is set but RT_JOTFORM_FIELDS is not');
  problems.forEach((problem) => {
    logger.warn(`site-config.js: ${problem}.`);
  });
}

export function readSiteConfig(settings: RuntimeSettings): SiteConfig {
  const config: SiteConfig = {
    ROUTING: settings.RT_ROUTING === 'path' ? 'path' : 'hash',
    FORMSPREE_ID: text(settings.RT_FORMSPREE_ID),
    HUBSPOT_PORTAL_ID: text(settings.RT_HUBSPOT_PORTAL_ID),
    HUBSPOT_FORM_ID: text(settings.RT_HUBSPOT_FORM_ID),
    FORM_EMAIL: text(settings.RT_FORM_EMAIL, DEFAULTS.FORM_EMAIL),
    FORM_EMAIL_ALIAS: text(settings.RT_FORM_EMAIL_ALIAS),
    JOTFORM_FORM_ID: text(settings.RT_JOTFORM_FORM_ID),
    JOTFORM_FIELDS: jotformFields(settings.RT_JOTFORM_FIELDS),
    GA4_ID: text(settings.RT_GA4_ID),
    PLATFORM_VIDEO_ID: text(settings.RT_PLATFORM_VIDEO_ID),
    MEDIUM_FEED: settings.RT_MEDIUM_FEED !== false,
    INSIGHTS_JSON_URL: text(settings.RT_INSIGHTS_JSON_URL),
    SECURITY_PDF_URL: text(settings.RT_SECURITY_PDF_URL),
    SALES_CALENDAR_URL: text(settings.RT_SALES_CALENDAR_URL, DEFAULTS.SALES_CALENDAR_URL),
    SALES_CALENDAR_EMBED_URL: text(settings.RT_SALES_CALENDAR_EMBED_URL, DEFAULTS.SALES_CALENDAR_EMBED_URL),
    LINKEDIN_URL: 'https://www.linkedin.com/company/retax360-llc',
    MEDIUM_URL: 'https://medium.com/@asula_74768',
    YOUTUBE_URL: 'https://www.youtube.com/@Retax360',
    INSTAGRAM_URL: 'https://www.instagram.com/retax360',
    SALES_EMAIL: 'sales@retax360.com',
    INFO_EMAIL: 'info@retax360.com',
    CAREERS_EMAIL: 'info@retax360.com',
    CAREERS_MAILTO: 'mailto:info@retax360.com?subject=ReTax360%20Careers',
    LEGAL_NAME: 'ReTax360 LLC',
    ADDRESS: {
      street: '4204 Glencoe Ave',
      city: 'Marina Del Rey',
      region: 'CA',
      postalCode: '90292',
      country: 'United States',
    },
    SITE_URL: 'https://www.retax360.com',
    COPYRIGHT_YEAR: 2026,
  };
  validate(config);
  return Object.freeze(config);
}

export const SITE_CONFIG: SiteConfig = readSiteConfig(typeof window === 'undefined' ? {} : window);
